import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
  EmptySocialSecurityNumber,
  isValidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {PhoneNumber, ValidPhoneNumber, EmptyPhoneNumber, isValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {StringUnion, NonEmptyString, EmptyString} from 'Core/models/EmptyString';
import {ZipCode, ValidZipCode, isValidZipCode, EmptyZipCode} from './ZipCode';
import {isValidDate} from './Date';

export const relations = [
  'Son',
  'Daughter',
  'Stepson',
  'Stepdaughter',
  'Spouse',
  'Child',
  'Granddaughter',
  'Grandson',
  'Domestic Partner',
  'Reciprocal Beneficiary',
  'Designated Beneficiary',
  'Other',
];

export enum ContingentBeneficiaryActionType {
  ADD_CONTINGENTBENEFICIARY = 'contingentBeneficiary/ADD_CONTINGENTBENEFICIARY',
  REMOVE_CONTINGENTBENEFICIARY = 'contingentBeneficiary/REMOVE_CONTINGENTBENEFICIARY',
  EMPTY_CONTINGENTBENEFICIARY = 'contingentBeneficiary/EMPTY_CONTINGENTBENEFICIARY',
  UPDATE_FIRST_NAME = 'contingentBeneficiary/UPDATE_FIRST_NAME',
  UPDATE_MIDDLE_NAME = 'contingentBeneficiary/UPDATE_MIDDLE_NAME',
  UPDATE_LAST_NAME = 'contingentBeneficiary/UPDATE_LAST_NAME',
  UPDATE_RELATIONSHIP = 'contingentBeneficiary/UPDATE_RELATIONSHIP',
  UPDATE_PERCENT = 'contingentBeneficiary/UPDATE_PERCENT',
  UPDATE_SSN = 'contingentBeneficiary/UPDATE_SSN',
  UPDATE_DOB = 'contingentBeneficiary/UPDATE_DOB',
  UPDATE_PHONE = 'contingentBeneficiary/UPDATE_PHONE',
  UPDATE_ADDRESS = 'contingentBeneficiary/UPDATE_ADDRESS',
  UPDATE_ADDRESS_LINE2 = 'contingentBeneficiary/UPDATE_ADDRESS_LINE2',
  UPDATE_CITY = 'contingentBeneficiary/UPDATE_CITY',
  UPDATE_STATE = 'contingentBeneficiary/UPDATE_STATE',
  UPDATE_ZIP = 'contingentBeneficiary/UPDATE_ZIP',
  EDIT_CONTINGENTBENEFICIARY = 'contingentBeneficiary/EDIT_CONTINGENTBENEFICIARY',
  UPDATE_SIGNATURE_DATE = 'contigentBeneficiary/UPDATE_SIGNATURE_DATE',
}

export class UnvalidatedContingentBeneficiary {
  public readonly key = 'UnvalidatedContingentBeneficiary';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

export class InvalidContingentBeneficiary {
  public readonly key = 'InvalidContingentBeneficiary';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

type AppProps = {
  firstName: StringUnion;
  middleName: StringUnion;
  lastName: StringUnion;
  relationship: StringUnion;
  percent: number;
  socialSecurityNumber: SocialSecurityNumber;
  dob: Date | '';
  phone: PhoneNumber;
  address: StringUnion;
  addressLine2: StringUnion;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  occSmokerCode: StringUnion;
  signatureDate: Date | '';
};

export class ValidContingentBeneficiary {
  public static createFromApp({
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate,
  }: AppProps) {
    return this.create(
      firstName,
      middleName,
      lastName,
      relationship,
      percent,
      socialSecurityNumber,
      dob,
      phone,
      address,
      addressLine2,
      city,
      state,
      zip,
      occSmokerCode,
      signatureDate,
    );
  }

  public static create(
    firstName: StringUnion,
    middleName: StringUnion,
    lastName: StringUnion,
    relationship: StringUnion,
    percent: number,
    socialSecurityNumber: SocialSecurityNumber,
    dob: Date | '',
    phone: PhoneNumber,
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    occSmokerCode: StringUnion,
    signatureDate: Date | '',
  ) {
    return isValidSocialSecurityNumber(socialSecurityNumber) &&
      isValidPhoneNumber(phone) &&
      isValidZipCode(zip) &&
      isValidDate(dob) &&
      !(firstName.value === '' || lastName.value === '' || relationship.value === '')
      ? new ValidContingentBeneficiary(
          firstName,
          middleName,
          lastName,
          relationship,
          percent,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          signatureDate,
        )
      : new InvalidContingentBeneficiary(
          firstName,
          middleName,
          lastName,
          relationship,
          percent,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          signatureDate,
        );
  }

  public readonly key = 'ValidContingentBeneficiary';
  private constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly percent: number,
    public readonly socialSecurityNumber: ValidSocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: ValidPhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly signatureDate: Date | '',
  ) {}
}

export type ContingentBeneficiary =
  | UnvalidatedContingentBeneficiary
  | InvalidContingentBeneficiary
  | ValidContingentBeneficiary;

export type ContingentBeneficiaryOptions<TReturn> = ToPartialMatchOptions<ContingentBeneficiary, 'key', TReturn>;

export const matchContingentBeneficiary = <TReturn>(options: ContingentBeneficiaryOptions<TReturn>) => (
  app: ContingentBeneficiary
): TReturn => {
  switch (app.key) {
    case 'InvalidContingentBeneficiary':
      return options.InvalidContingentBeneficiary ? options.InvalidContingentBeneficiary(app) : options.default(app);
    case 'UnvalidatedContingentBeneficiary':
      return options.UnvalidatedContingentBeneficiary
        ? options.UnvalidatedContingentBeneficiary(app)
        : options.default(app);
    case 'ValidContingentBeneficiary':
      return options.ValidContingentBeneficiary ? options.ValidContingentBeneficiary(app) : options.default(app);
    default:
      return options.default(app);
  }
};

export const changeFirstName = (firstName: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    firstName: NonEmptyString.create(firstName),
  });

export const changeMiddleName = (middleName: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    middleName: NonEmptyString.create(middleName),
  });

export const changeLastName = (lastName: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    lastName: NonEmptyString.create(lastName),
  });

export const changeRelationship = (relationship: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    relationship: NonEmptyString.create(relationship),
  });

export const changePercent = (percent: number) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    percent: percent,
  });

export const changeSSN = (socialSecurityNumber: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });

export const changeDOB = (dob: Date | '') => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    dob: dob,
  });

export const changePhone = (phone: PhoneNumber) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    phone: phone,
  });

export const changeAddress = (address: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    address: NonEmptyString.create(address),
  });

export const changeAddressLine2 = (addressLine2: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    addressLine2: NonEmptyString.create(addressLine2),
  });

export const changeCity = (city: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    city: NonEmptyString.create(city),
  });

export const changeState = (state: string) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    state: NonEmptyString.create(state),
  });

export const changeZip = (zip: ZipCode) => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    zip,
  });

export const changeOccSmokerCode = (occSmokerCode: string) => (app: ValidContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({...app, occSmokerCode: NonEmptyString.create(occSmokerCode)});

export const changeSignatureDate = (signatureDate: Date | '') => (app: ContingentBeneficiary) =>
  ValidContingentBeneficiary.createFromApp({
    ...app,
    signatureDate,
  });

export const updateContingentBeneficiaryValues = (
  firstName: StringUnion,
  middleName: StringUnion,
  lastName: StringUnion,
  relationship: StringUnion,
  percent: number,
  socialSecurityNumber: string,
  dob: Date | '',
  phone: PhoneNumber,
  address: StringUnion,
  addressLine2: StringUnion,
  city: StringUnion,
  state: StringUnion,
  zip: ZipCode,
  occSmokerCode: StringUnion,
  signatureDate: Date | '',
) => () =>
  ValidContingentBeneficiary.create(
    firstName,
    middleName,
    lastName,
    relationship,
    percent,
    ValidSocialSecurityNumber.create(socialSecurityNumber),
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    signatureDate,
  );

export const EmptyContingentBeneficiary = new UnvalidatedContingentBeneficiary(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  0,
  EmptySocialSecurityNumber,
  '',
  EmptyPhoneNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  new EmptyString(),
  '',
);

export const isInvalidContingentBeneficiary = (ele: ContingentBeneficiary) => {
  return ele.key === 'InvalidContingentBeneficiary' || ele.key === 'UnvalidatedContingentBeneficiary';
};

export const MAX_CONTINGENT_BENEFICIARY = 8;
